import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentStaff, useStaffLogout, getStaffRedirectUrl } from '@/hooks/useStaffAuth';
import { useTables } from '@/hooks/useTables';
import { useOrders } from '@/hooks/useOrders';
import { TableStatusBadge } from '@/components/common/TableStatusBadge';
import { StaffBurgerMenu } from '@/components/layout/StaffBurgerMenu';
import { LogOut, Users, Utensils, Receipt, Languages, Printer, CheckCircle } from 'lucide-react';
import type { RestaurantTable } from '@/types/entities';

// Print styles for thermal receipt printers (58mm/80mm)
const printStyles = `
  @media print {
    @page {
      size: 80mm auto;
      margin: 3mm;
    }

    /* Hide everything by default */
    body * {
      visibility: hidden;
    }

    /* Show only the print container */
    #print-receipt,
    #print-receipt * {
      visibility: visible;
    }

    #print-receipt {
      position: absolute;
      left: 0;
      top: 0;
      width: 74mm;
      padding: 2mm;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.4;
      color: black;
      background: white;
    }
  }

  /* Hide print container on screen */
  #print-receipt {
    display: none;
  }

  @media print {
    #print-receipt {
      display: block !important;
    }
  }
`;

function PrinterDashboardContent() {
  const { t, i18n } = useTranslation();
  const { data: staff } = useCurrentStaff();
  const staffLogout = useStaffLogout();
  const { data: tables, isLoading } = useTables(staff?.restaurantId || null);
  const { data: orders = [] } = useOrders(staff?.restaurantId || null);

  const [selectedTableForBill, setSelectedTableForBill] = useState<RestaurantTable | null>(null);
  const [pendingAutoPrint, setPendingAutoPrint] = useState<string | null>(null);

  // Track printed order IDs in localStorage so the status survives page refresh
  const [printedOrderIds, setPrintedOrderIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('printer_dashboard_printed');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const prevAllReadyRef = useRef<Record<string, boolean>>({});
  const isInitializedRef = useRef(false);

  // Get the last (most recent) parent order for a table - regardless of status
  const getLastTableOrder = (tableId: string) => {
    const tableOrders = orders
      .filter((order) => order.tableId === tableId && !order.parentOrderId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return tableOrders[0] || null;
  };

  const getLastTableOrderForBill = (tableId: string) => {
    const lastOrder = getLastTableOrder(tableId);
    return lastOrder ? [lastOrder] : [];
  };

  const markAsPrinted = (orderId: string) => {
    setPrintedOrderIds((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      try {
        localStorage.setItem('printer_dashboard_printed', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const triggerPrintForTable = (table: RestaurantTable) => {
    setSelectedTableForBill(table);
    setPendingAutoPrint(table.id);
  };

  // Inject print styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = printStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Monitor orders for false→true allSubOrdersReady transitions to trigger auto-print
  useEffect(() => {
    if (!tables || !orders) return;

    for (const table of tables) {
      const lastOrder = getLastTableOrder(table.id);
      if (!lastOrder) {
        prevAllReadyRef.current[table.id] = false;
        continue;
      }
      const allSubOrders = lastOrder.subOrders || [];
      const activeSubOrders = allSubOrders.filter((so: any) => so.status !== 'cancelled');
      const isAllReady =
        activeSubOrders.length > 0 && activeSubOrders.every((so: any) => so.status === 'ready');
      const wasAllReady = prevAllReadyRef.current[table.id] ?? false;

      if (isInitializedRef.current && isAllReady && !wasAllReady) {
        triggerPrintForTable(table);
      }

      prevAllReadyRef.current[table.id] = isAllReady;
    }

    isInitializedRef.current = true;
  }, [orders, tables]);

  // Trigger window.print() after DOM renders the receipt
  useEffect(() => {
    if (!pendingAutoPrint) return;
    if (selectedTableForBill?.id !== pendingAutoPrint) return;

    const timer = setTimeout(() => {
      window.print();
      setPendingAutoPrint(null);
    }, 150);

    return () => clearTimeout(timer);
  }, [pendingAutoPrint, selectedTableForBill]);

  // Mark as printed and clean up after print completes or is cancelled
  useEffect(() => {
    if (!pendingAutoPrint) return;

    const tableAtPrint = selectedTableForBill;

    const handleAfterPrint = () => {
      if (tableAtPrint) {
        const lastOrder = getLastTableOrder(tableAtPrint.id);
        if (lastOrder) markAsPrinted(lastOrder.id);
      }
      setSelectedTableForBill(null);
      setPendingAutoPrint(null);
    };

    window.addEventListener('afterprint', handleAfterPrint, { once: true });
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [pendingAutoPrint]);

  const handleLogout = async () => {
    await staffLogout.mutateAsync();
    window.location.href = getStaffRedirectUrl();
  };

  // Calculate total amount for a single order (excluding cancelled sub-orders)
  const getOrderTotal = (parentOrder: any) => {
    return (parentOrder.subOrders || [])
      .filter((subOrder: any) => subOrder.status !== 'cancelled')
      .reduce((subTotal: number, subOrder: any) => {
        const subOrderTotal =
          subOrder.items?.reduce((sum: number, item: any) => {
            const price = item.priceAtOrder || item.menuItem?.price || 0;
            return sum + price * item.quantity;
          }, 0) || 0;
        return subTotal + subOrderTotal;
      }, 0);
  };

  // Get total order count for a single order
  const getOrderItemCount = (parentOrder: any) => {
    return (parentOrder.subOrders || [])
      .filter((so: any) => so.status !== 'cancelled')
      .reduce((count: number, subOrder: any) => {
        return count + (subOrder.items?.length || 0);
      }, 0);
  };

  if (!staff) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold">{t('staff.printerDashboard')}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {t('common.welcome')}, {staff.username}
            </p>
          </div>
          {/* Mobile Burger Menu */}
          <div className="md:hidden">
            <StaffBurgerMenu onLogout={handleLogout} />
          </div>
          {/* Desktop Language & Logout */}
          <div className="hidden md:flex items-center gap-2">
            <Select value={i18n.language} onValueChange={(lang) => i18n.changeLanguage(lang)}>
              <SelectTrigger className="w-[140px]">
                <Languages className="w-4 h-4 me-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Francais</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 me-2" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xl font-semibold mb-4">{t('tables.selectTable')}</h2>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
        ) : tables && tables.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {tables.map((table) => {
              const lastOrder = getLastTableOrder(table.id);
              const hasLastOrder = lastOrder !== null;
              const orderTotal = hasLastOrder ? getOrderTotal(lastOrder) : 0;
              const itemCount = hasLastOrder ? getOrderItemCount(lastOrder) : 0;
              const isPrinted = hasLastOrder && printedOrderIds.has(lastOrder.id);

              // Check sub-orders status for visual indicator
              const allSubOrders = hasLastOrder ? (lastOrder.subOrders || []) : [];
              const activeSubOrders = allSubOrders.filter((so: any) => so.status !== 'cancelled');
              const allSubOrdersReady =
                activeSubOrders.length > 0 &&
                activeSubOrders.every((so: any) => so.status === 'ready');

              // Get order status for badge
              const orderStatus = lastOrder?.status || 'available';
              const isCheckedOrCompleted = orderStatus === 'completed' || orderStatus === 'checked';

              return (
                <Card
                  key={table.id}
                  className={`transition-shadow ${
                    allSubOrdersReady && !isCheckedOrCompleted
                      ? 'border-2 border-green-500 shadow-green-200'
                      : ''
                  } ${isCheckedOrCompleted ? 'opacity-70 border-gray-300' : ''} ${!hasLastOrder ? 'opacity-50' : ''}`}
                >
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className={`flex items-center justify-center w-12 h-12 rounded-full ${
                                isCheckedOrCompleted
                                  ? 'bg-gray-200'
                                  : allSubOrdersReady
                                    ? 'bg-green-500/20'
                                    : 'bg-primary/10'
                              }`}
                            >
                              <Utensils
                                className={`w-6 h-6 ${
                                  isCheckedOrCompleted
                                    ? 'text-gray-500'
                                    : allSubOrdersReady
                                      ? 'text-green-600'
                                      : 'text-primary'
                                }`}
                              />
                            </div>
                            <div>
                              <div className="text-2xl font-bold">
                                {t('table.table')} {table.tableNumber || '?'}
                              </div>
                              {hasLastOrder && lastOrder.serverUsername && (
                                <div className="text-sm text-muted-foreground">
                                  {lastOrder.serverUsername}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">
                                {table.seats} {t('table.seats')}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {hasLastOrder && (
                                <>
                                  <Badge
                                    variant={isCheckedOrCompleted ? 'secondary' : 'default'}
                                    className={isCheckedOrCompleted ? 'bg-gray-200 text-gray-600' : ''}
                                  >
                                    {t(`order.statuses.${orderStatus}`)}
                                  </Badge>
                                  <Badge variant="secondary">
                                    {itemCount} {t('order.items')}
                                  </Badge>
                                </>
                              )}
                              {!hasLastOrder && <TableStatusBadge status="available" />}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Total Price */}
                      {hasLastOrder && orderTotal > 0 && (
                        <div className="pt-3 border-t">
                          <div
                            className={`p-2 rounded-lg ${isCheckedOrCompleted ? 'bg-gray-100' : 'bg-primary/10'}`}
                          >
                            <div className="flex items-center justify-between font-semibold">
                              <span className="flex items-center gap-2 text-sm">
                                <Receipt className="w-4 h-4" />
                                {t('order.total')}
                              </span>
                              <span className="text-base font-bold">
                                {orderTotal.toFixed(2)} DH
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {!hasLastOrder && (
                        <div className="text-center text-sm text-muted-foreground">
                          {t('order.noOrders')}
                        </div>
                      )}

                      {/* Print status */}
                      {hasLastOrder && (
                        <div className="pt-2 border-t">
                          {isPrinted ? (
                            <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-medium py-1">
                              <CheckCircle className="w-4 h-4" />
                              {t('staff.printBill')} ✓
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={() => triggerPrintForTable(table)}
                            >
                              <Printer className="w-4 h-4 me-2" />
                              {t('staff.printBill')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">{t('tables.noTables')}</div>
        )}
      </div>

      {/* Print-only Receipt Container (hidden on screen, visible on print) */}
      {selectedTableForBill && (
        <div id="print-receipt">
          {/* Custom Receipt Header */}
          {staff?.receiptHeader && (
            <div
              style={{
                textAlign: 'center',
                marginBottom: '8px',
                whiteSpace: 'pre-wrap',
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            >
              {staff.receiptHeader}
            </div>
          )}

          <div
            style={{
              textAlign: 'center',
              borderBottom: '1px dashed black',
              paddingBottom: '8px',
              marginBottom: '8px',
            }}
          >
            <div style={{ fontSize: '15px', marginTop: '4px' }}>{new Date().toLocaleString()}</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('order.totalBill')} - {t('table.table')} #{selectedTableForBill.tableNumber}
            {(() => {
              const lastOrder = getLastTableOrderForBill(selectedTableForBill.id)[0];
              return lastOrder?.serverUsername ? (
                <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px' }}>
                  {t('order.server')}: {lastOrder.serverUsername}
                </div>
              ) : null;
            })()}
          </div>

          <div
            style={{ borderBottom: '1px dashed black', paddingBottom: '8px', marginBottom: '8px' }}
          >
            {getLastTableOrderForBill(selectedTableForBill.id).flatMap((parentOrder) =>
              (parentOrder.subOrders || [])
                .filter((so: any) => so.status !== 'cancelled')
                .flatMap((subOrder: any) => {
                  const groupedItems = (subOrder.items || []).reduce(
                    (acc: Record<string, any>, item: any) => {
                      const key = `${item.menuItemId}-${item.menuItemOptionId || 'no-option'}`;
                      if (!acc[key]) {
                        acc[key] = { ...item, quantity: 0 };
                      }
                      acc[key].quantity += item.quantity;
                      return acc;
                    },
                    {} as Record<string, any>
                  );
                  return Object.values(groupedItems).map((item: any) => (
                    <div
                      key={`${parentOrder.id}-${subOrder.id}-${item.id}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '2px 0',
                        fontSize: '18px',
                        fontWeight: 'bold',
                      }}
                    >
                      <span>
                        {item.quantity}x{' '}
                        {i18n.language === 'ar' ? item.menuItem?.nameAr : item.menuItem?.nameFr}
                        {item.menuItemOptionId && item.menuItemOption && (
                          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                            {' '}
                            (
                            {i18n.language === 'ar'
                              ? item.menuItemOption?.nameAr
                              : item.menuItemOption?.nameFr}
                            )
                          </span>
                        )}
                      </span>
                      <span>
                        {((item.priceAtOrder || item.menuItem?.price || 0) * item.quantity).toFixed(
                          2
                        )}{' '}
                        DH
                      </span>
                    </div>
                  ));
                })
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold',
              fontSize: '20px',
              borderTop: '2px solid black',
              paddingTop: '8px',
            }}
          >
            <span>{t('order.grandTotal')}</span>
            <span>
              {getLastTableOrderForBill(selectedTableForBill.id)
                .reduce((total, parentOrder) => {
                  return total + getOrderTotal(parentOrder);
                }, 0)
                .toFixed(2)}{' '}
              DH
            </span>
          </div>

          {/* Custom Receipt Footer */}
          {staff?.receiptFooter && (
            <div
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                marginTop: '50px',
                paddingTop: '8px',
                borderTop: '1px dashed black',
                whiteSpace: 'pre-wrap',
                fontSize: '14px',
              }}
            >
              {staff.receiptFooter}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PrinterDashboard() {
  const { data: staff } = useCurrentStaff();

  if (!staff) {
    return null;
  }

  return <PrinterDashboardContent />;
}
